/**
 * Client for /api/orgs/:action (Organisations — docs/engineering/ORG_ACCOUNTS_PLAN.md).
 *
 * Every org change goes through the server — the browser has no write access
 * to orgs, members or invitations. Each call carries the Clerk session token;
 * the endpoint verifies it and checks the caller's role.
 */

import type { ReportRow } from '../../api/_lib/launchModel'
import type { OrgRole } from '../../api/_lib/orgActions'
import { withLearnPlaces } from './learnLocal'
import type { AttrDef, Condition, Group, Person, PersonList, Value } from '../../api/_lib/attributesModel'

export type { OrgRole }

export type JoinedVia = 'signup' | 'invite' | 'csv' | 'code'

export interface OrgMember {
  userId: string
  roles: OrgRole[]
  state: 'active' | 'suspended'
  /** The name set inside the organisation, else the account name. */
  name: string | null
  accountName: string | null
  displayName: string | null
  language: string | null
  externalId: string | null
  email: string | null
  imageUrl: string | null
  joinedVia: JoinedVia | null
  joinedAt: string | null
  isYou: boolean
}

export interface OrgInvite {
  id: string; email: string; roles: OrgRole[]; via: 'email' | 'csv'
  displayName: string | null; externalId: string | null; invitedBy: string | null
  sentAt: string | null; expiresAt: string | null; expired: boolean
}

export interface OrgSettings {
  id: string; name: string; code: string | null
  defaultLanguage: string | null; timezone: string | null; country: string | null
  codeJoiningEnabled: boolean
  createdAt: string | null
  logoUrl: string | null
  /** Public contact address shown to people turned away (16, 20 Oct 2026). */
  contactEmail?: string | null
}

export interface DeclinedInvite {
  id: string; email: string; roles: OrgRole[]; via: 'email' | 'csv'; displayName: string | null
  invitedBy: string | null; sentAt: string | null
}

export interface AcceptedInvite {
  id: string; email: string; roles: OrgRole[]; via: 'email' | 'csv'; displayName: string | null
  invitedBy: string | null; sentAt: string | null; acceptedAt: string | null
}

export interface OrgList {
  org: OrgSettings
  members: OrgMember[]
  invites: OrgInvite[]
  acceptedInvites: AcceptedInvite[]
  declinedInvites: DeclinedInvite[]
  transfer: { id: string; toUser: string; toName: string | null } | null
  /** People who used the organisation code without an invitation (Members → Waiting for approval). */
  joinRequests?: JoinRequest[]
}

export interface JoinRequest { id: string; userId: string; name: string | null; email: string | null; imageUrl: string | null; requestedAt: string | null }

/** What using an organisation code did: joined (they were invited), or asked the admins. */
export interface CodeJoin { org: { id: string; name: string }; joined?: boolean; requested?: boolean; roles?: OrgRole[] }

export interface InviteResult { invite: { id: string; email: string; roles: OrgRole[]; expiresAt: string }; link: string; emailed: boolean }

export interface CsvRow { email: string; name?: string; roles?: OrgRole[]; externalId?: string; attributes?: Record<string, Value>; groupIds?: string[] }
export interface CsvResult {
  results: Array<{ row: number; email: string; outcome: 'invited' | 'updated' | 'error'; message: string | null; emailed?: boolean }>
  invited: number; updated: number; failed: number
}

export interface InviteInfo {
  orgId: string; orgName: string; email: string; roles: OrgRole[]; invitedBy: string | null
  state: 'pending' | 'accepted' | 'revoked' | 'declined'; expired: boolean; emailMatches: boolean; alreadyMember: boolean
}

/** What the invitation link shows before sign-in. */
export interface InvitePreview {
  orgId: string; orgName: string; logoUrl: string | null; email: string; roles: OrgRole[]; invitedBy: string | null
  state: 'pending' | 'accepted' | 'revoked' | 'declined'; expired: boolean
  /** Signed in: is it for one of my addresses? (If false, `email` comes back masked.) Signed out: null. */
  forYou: boolean | null
  /** Signed in as the invitee, already a member with every role this invite gives. */
  alreadyMember?: boolean
  /** Their roles there, when alreadyMember. */
  myRoles?: OrgRole[] | null
}

export interface PendingTransfer { id: string; orgId: string; orgName: string; fromName: string }

export class OrgApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

/** Same accessor `supabase.ts` uses — Clerk attaches itself to window on mount. */
async function bearer(fresh = false): Promise<string> {
  const w = window as unknown as { Clerk?: { session?: { getToken: (o?: { skipCache?: boolean }) => Promise<string | null> } } }
  const token = await w.Clerk?.session?.getToken(fresh ? { skipCache: true } : undefined)
  if (!token) throw new OrgApiError(401, 'You need to be signed in.')
  return token
}

/**
 * signedOut: send no session. maybeSignedIn: send it if there is one — for the
 * public invitation actions, where the server answers differently for the invitee.
 */
async function call<T>(action: string, body: Record<string, unknown>, opts: { signedOut?: boolean; maybeSignedIn?: boolean; keepalive?: boolean } = {}, fresh = false): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  if (opts.maybeSignedIn) {
    const t = await bearer(fresh).catch(() => null)
    if (t) headers.authorization = `Bearer ${t}`
  } else if (!opts.signedOut) headers.authorization = `Bearer ${await bearer(fresh)}`
  const json = JSON.stringify(body)
  const res = await fetch(`/api/orgs/${action}`, {
    method: 'POST',
    headers,
    body: json,
    // keepalive: the browser finishes sending even if the tab closes straight after (27 Oct 2026 —
    // a learner who closed the tab on "You passed" could lose the completion). Browsers refuse a
    // keepalive body over 64 KB outright, so a larger one goes as a normal request.
    keepalive: !!opts.keepalive && json.length < 60_000,
  })
  const data = await res.json().catch(() => ({})) as { ok?: boolean; error?: string } & T
  // A token that ran out while the tab sat in the background (23 Oct 2026: "invalid token" on Cancel
  // request, after working in another window — browsers slow a hidden tab's timers, so Clerk's
  // minute-long token can be stale on the first click back). Once, with a token fetched afresh.
  if (res.status === 401 && data.error === 'invalid token' && headers.authorization && !fresh) return call<T>(action, body, opts, true)
  if (!res.ok || data.ok === false) throw new OrgApiError(res.status, data.error ?? 'Something went wrong')
  return data
}

export interface MyOrg { id: string; name: string; code: string; roles: OrgRole[]; logoUrl?: string | null
  /** The organisation's Default course language — a new course starts in it (20 Oct 2026). */
  defaultLanguage?: string | null }

export interface BootstrapResult {
  orgs: MyOrg[]
  activeOrgId: string | null
  /** True when this sign-in created the user's organisation. */
  created: boolean
  /** What moved into the org on this sign-in (existing users), else null. */
  adopted: Record<string, number> | null
  /** Memberships that exist but are suspended right now. */
  paused: Array<{ id: string; name: string }>
  /** Organisations this user was removed from (G3). */
  removed?: Array<{ id: string; name: string }>
  /** Organisations someone wants to hand to this user. */
  transfers: PendingTransfer[]
  /** Invitations to this user's addresses still waiting (shown in the bell). */
  invites?: WaitingInvite[]
  /** First sign-in with invitations waiting: nothing was created — ask Join or create your own. */
  invitedFirst?: boolean
  /** Organisations they asked to join with a code, waiting for an admin. */
  requested?: Array<{ id: string; name: string; contactEmail?: string | null }>
}

/** The organisation behind a code, as a signed-in person sees it. */
export interface FoundOrg {
  id: string; name: string; logoUrl: string | null; members: number
  alreadyMember?: boolean; myRoles?: OrgRole[]
  /** They have an invitation here — using the code joins them straight away. */
  invited?: boolean
  /** They already asked; an admin hasn't answered yet. */
  requested?: boolean
}

export interface WaitingInvite { id: string; orgId: string; orgName: string; logoUrl: string | null; invitedBy: string | null; roles: OrgRole[]; sentAt?: string | null }

/** Progress saves still on their way (learn mode). Course lists read after them — see myLearning. */
let learnSaves: Promise<unknown> = Promise.resolve()
export function waitForLearnSave(p: Promise<unknown>): void {
  const before = learnSaves
  learnSaves = Promise.all([before, p.catch(() => {})])
}

/**
 * The last My learning answer, per organisation (24 Oct 2026): Home's Continue / Due and a course
 * page's "You're taking this course" drew last — that answer brings everyone's launches up to date
 * first, about a second. They now show the last answer at once and the fresh one replaces it.
 * Kept in memory and in this tab's sessionStorage (a reload keeps it; another tab or sign-in doesn't).
 */
const learnCache = new Map<string, LearningItem[]>()
const learnInFlight = new Map<string, Promise<{ items: LearningItem[] }>>()
const LEARN_CACHE_KEY = (orgId: string) => `lb_my_learning_${orgId}`
function rememberLearning(orgId: string, items: LearningItem[]) {
  learnCache.set(orgId, items)
  try { sessionStorage.setItem(LEARN_CACHE_KEY(orgId), JSON.stringify(items)) } catch { /* full or private: memory only */ }
}
/** The last My learning answer for this organisation, or null if there is none yet. */
export function cachedMyLearning(orgId: string | null): LearningItem[] | null {
  if (!orgId) return null
  const hit = learnCache.get(orgId)
  if (hit) return withLearnPlaces(hit)
  try {
    const raw = sessionStorage.getItem(LEARN_CACHE_KEY(orgId))
    if (!raw) return null
    const items = JSON.parse(raw) as LearningItem[]
    learnCache.set(orgId, items)
    return withLearnPlaces(items)
  } catch { return null }
}

export const orgApi = {
  /** Run on every sign-in. Pass `joining: true` when arriving through an invite or an org code. */
  bootstrap: (opts: { joining?: boolean; createOwn?: boolean } = {}) => call<BootstrapResult>('bootstrap', opts),
  /** Remember the organisation the user switched to. */
  setActive: (orgId: string) => call<{ activeOrgId: string }>('set-active', { orgId }),
  /** Which organisation does this code belong to? (signed-in only) */
  findCode: (code: string) => call<{ org: FoundOrg }>('find-code', { code }),
  /** Name + logo behind a code — works signed out, for the join page. */
  peekCode: (code: string) => call<{ org: { name: string; logoUrl: string | null } }>('peek-code', { code }, { signedOut: true }),
  /** Use an organisation code: invited → joined; otherwise a request for the admins to approve. */
  joinCode: (code: string) => call<CodeJoin>('join-code', { code }),
  /** Members → Waiting for approval. `roles` defaults to Learner. */
  approveJoinRequest: (orgId: string, requestId: string, roles: OrgRole[]) => call<object>('approve-join-request', { orgId, requestId, roles }),
  declineJoinRequest: (orgId: string, requestId: string) => call<object>('decline-join-request', { orgId, requestId }),
  create: (p: { name: string; defaultLanguage?: string | null; timezone?: string | null; country?: string | null }) =>
    call<{ org: { id: string; name: string; code: string | null } }>('create', p),
  /** Send only the fields that change. */
  update: (orgId: string, patch: {
    name?: string; defaultLanguage?: string | null; timezone?: string | null
    country?: string | null; codeJoiningEnabled?: boolean; contactEmail?: string | null
  }) => call<{ org: OrgSettings }>('update', { orgId, ...patch }),
  /** PNG, JPG, WebP or SVG as a data URL, up to 2 MB. */
  setLogo: (orgId: string, dataUrl: string) => call<{ logoUrl: string }>('set-logo', { orgId, dataUrl }),
  removeLogo: (orgId: string) => call<object>('remove-logo', { orgId }),
  regenerateCode: (orgId: string) => call<{ code: string }>('regenerate-code', { orgId }),
  list: (orgId: string) => call<OrgList>('list', { orgId }),
  /** `attributes` (by key) and `groupIds` (user groups) are set when they join. */
  invite: (orgId: string, p: { email: string; roles: OrgRole[]; displayName?: string; externalId?: string; attributes?: Record<string, Value>; groupIds?: string[]; againOf?: string }) =>
    call<InviteResult>('invite', { orgId, ...p }),
  inviteCsv: (orgId: string, rows: CsvRow[]) => call<CsvResult>('invite-csv', { orgId, rows }),
  resendInvite: (orgId: string, inviteId: string) =>
    call<{ link: string; emailed: boolean; expiresAt: string }>('resend-invite', { orgId, inviteId }),
  /** A fresh link to copy (the previous one stops working); no email is sent. */
  inviteLink: (orgId: string, inviteId: string) =>
    call<{ link: string; expiresAt: string }>('invite-link', { orgId, inviteId }),
  revokeInvite: (orgId: string, inviteId: string) =>
    call<object>('revoke-invite', { orgId, inviteId }),
  findInvite: (token: string) => call<{ invite: InviteInfo }>('find-invite', { token }),
  /** No sign-in needed — the token is the invitee's secret. */
  peekInvite: (token: string) => call<{ invite: InvitePreview }>('peek-invite', { token }, { maybeSignedIn: true }),
  declineInvite: (token: string) => call<object>('decline-invite', { token }, { maybeSignedIn: true }),
  /** An expired invitation: ask the organisation's admins for a new one (their bell). */
  requestReinvite: (token: string) => call<{ admins: number }>('request-reinvite', { token }, { maybeSignedIn: true }),
  acceptInvite: (token: string) => call<{ org: { id: string; name: string } }>('accept-invite', { token }),
  setRoles: (orgId: string, userId: string, roles: OrgRole[]) => call<{ roles: OrgRole[] }>('set-roles', { orgId, userId, roles }),
  /** Send only the fields that change; '' clears. */
  updateMember: (orgId: string, userId: string, patch: { displayName?: string; language?: string; externalId?: string }) =>
    call<object>('update-member', { orgId, userId, ...patch }),
  suspend: (orgId: string, userId: string) => call<object>('suspend', { orgId, userId }),
  reactivate: (orgId: string, userId: string) => call<object>('reactivate', { orgId, userId }),
  remove: (orgId: string, userId: string) => call<object>('remove', { orgId, userId }),
  transfer: (orgId: string, userId: string) => call<{ transfer: { id: string } }>('transfer', { orgId, userId }),
  cancelTransfer: (orgId: string) => call<object>('cancel-transfer', { orgId }),
  /** Join or decline an invitation from the bell (it must be for one of my addresses). */
  /** A paused member asks the organisation's admins to turn their access back on (G2). */
  /** `active`: their access was already turned back on — nothing was asked; open the organisation. */
  requestAccess: (orgId: string) => call<{ admins: number; active?: boolean }>('request-access', { orgId }),
  respondInvite: (orgId: string, inviteId: string, accept: boolean) =>
    call<{ accepted: boolean; org?: { id: string; name: string } }>('respond-invite', { orgId, inviteId, accept }),
  respondTransfer: (transferId: string, accept: boolean) =>
    call<{ accepted: boolean; orgId?: string }>('respond-transfer', { transferId, accept }),

  // ── Attributes & groups (S3 — api/_lib/attrActions.ts) ──
  /** Everything for the admin screens: attributes, people with their values, groups, lists. */
  attrsLoad: (orgId: string) => call<AttrBundle>('attrs-load', { orgId }),
  /** The signed-in member's own details in this organisation. */
  attrsMine: (orgId: string) => call<MyAttrs>('attrs-mine', { orgId }),
  attrSave: (orgId: string, def: AttrDef, deletedOptionIds: string[] = []) => call<object>('attr-save', { orgId, def, deletedOptionIds }),
  attrDelete: (orgId: string, key: string) => call<object>('attr-delete', { orgId, key }),
  /** null / '' clears a value. A member may only set their own, and only what they may edit. */
  valuesSet: (orgId: string, userId: string, values: Record<string, Value>) => call<object>('values-set', { orgId, userId, values }),
  eligibleSet: (orgId: string, list: PersonList, change: { add?: string[]; remove?: { userId: string; reassignTo?: string | null } }) =>
    call<object>('eligible-set', { orgId, list, ...change }),
  groupSave: (orgId: string, group: Partial<Group> & { kind: Group['kind']; name: string }) => call<{ id: string }>('group-save', { orgId, group }),
  csvColumnsSet: (orgId: string, keys: string[]) => call<object>('csv-columns-set', { orgId, keys }),

  // ── Launches, learning, reports (S4 — api/_lib/launchActions.ts) ──
  launchReach: (orgId: string, targets: LaunchTargetInput[], courseId?: string) => call<{ total: number; overlap: number; people: Array<{ id: string; name: string; email: string }>; alreadyCompleted: number }>('launch-reach', { orgId, targets, courseId }),
  launchSave: (orgId: string, launch: LaunchInput) => call<{ launchId: string; status?: string; reached?: number; alreadyCompleted?: number }>('launch-save', { orgId, launch }),
  launchEnd: (orgId: string, launchId: string) => call<object>('launch-end', { orgId, launchId }),
  launchCancel: (orgId: string, launchId: string) => call<object>('launch-cancel', { orgId, launchId }),
  launchesOfCourse: (orgId: string, courseId: string) => call<{ launches: CourseLaunch[]; totals: LaunchTotals }>('launches-of-course', { orgId, courseId }),
  launchSummary: (orgId: string) => call<{ courses: Record<string, { launched: boolean; people: number; active: number }> }>('launch-summary', { orgId }),
  // Waits for any progress the player is still saving, so a page opened straight after leaving a
  // course shows where the learner got to, not the moment before (13 Oct 2026).
  // Calls made while one is on its way share its answer (24 Oct 2026): the organisation's first load
  // starts one early, and Home / the course page then wait on that rather than send their own.
  myLearning: (orgId: string) => {
    const going = learnInFlight.get(orgId)
    if (going) return going
    const p = learnSaves.then(() => call<{ items: LearningItem[] }>('my-learning', { orgId })).then(r => {
      const items = withLearnPlaces(r.items)
      rememberLearning(orgId, items)
      return { ...r, items }
    }).finally(() => learnInFlight.delete(orgId))
    learnInFlight.set(orgId, p)
    return p
  },
  progressReport: (orgId: string, courseId: string, events: PlayerEvent[], launchId?: string, device?: 'phone' | 'tablet' | 'computer') =>
    call<ProgressResult>('progress-report', { orgId, courseId, events, launchId, device }, { keepalive: true }),
  /** Before this sitting: each question's latest answer (12 Oct 2026). */
  /** `attempt: 'completed'`: the last finished attempt rather than the current one (23 Oct 2026). */
  learnState: (orgId: string, courseId: string, attempt?: 'completed') => call<LearnState>('learn-state', { orgId, courseId, ...(attempt ? { attempt } : {}) }),
  // The bell, on the server (13 Oct 2026 — migration 14): the caller's own notifications.
  notificationsList: () => call<{ notifications: ServerNotification[] }>('notifications-list', {}),
  notificationsRead: (ids: string[], read = true) => call<object>('notifications-read', { ids, read }),
  notificationsReadAll: () => call<object>('notifications-read-all', {}),
  notificationsDelete: (ids: string[] | 'all') => call<object>('notifications-delete', ids === 'all' ? { all: true } : { ids }),
  notificationCreate: (n: { kind: 'translation_done' | 'info'; title: string; body?: string; link?: string }) => call<object>('notification-create', n),
  reportData: (orgId: string) => call<ReportData>('report-data', { orgId }),
  /** Who would lose a course if this launch audience / this group were saved (22 Oct 2026). */
  audienceLeavers: (orgId: string, change: { launchId: string; targets: LaunchTargetInput[] } | { group: { id?: string; rule?: unknown; members?: string[] } } | { people: Array<{ userId: string; values: Record<string, unknown> }> }) =>
    call<{ courses: AudienceLeavers }>('audience-leavers', { orgId, ...change }),
  reportActivity: (orgId: string, userId: string) => call<{ events: Array<{ event: string; courseId: string; at: string; data: Record<string, unknown>; device: string | null }> }>('report-activity', { orgId, userId }),
}

/** A launch target as the Launch flow sends it. */
export type LaunchTargetInput =
  | { kind: 'everyone' } | { kind: 'group'; groupId: string } | { kind: 'member'; userId: string }
  | { kind: 'attribute'; rule: Condition[] }
export interface LaunchInput {
  id?: string; courseId?: string; languages?: string[]; targets: LaunchTargetInput[]
  required?: boolean; startAt?: string | null; dueAt?: string | null; dueDays?: number | null; notify?: boolean
  /** Notifications: a reminder this many days before the due date (null = none); an overdue email. */
  remindDays?: number | null; notifyOverdue?: boolean
  /** A scheduled launch: email them now that it is assigned (as well as when it starts). */
  notifyAssigned?: boolean
}
export interface CourseLaunch {
  id: string; status: 'scheduled' | 'active' | 'ended' | 'cancelled'; required: boolean; startAt: string; dueAt: string | null; dueDays: number | null
  notify: boolean; notifyAssigned?: boolean; remindDays: number | null; notifyOverdue: boolean; endedAt: string | null; createdAt: string
  languages: Array<{ courseId: string; language: string }>
  targets: Array<{ id: string; kind: string; groupId: string | null; rule: Condition[] | null; userId: string | null }>
  people: number; completed: number; overdue: number
}
/** The course page's Overview tiles — each person counted once across launches. */
export interface LaunchTotals { reached: number; completed: number; averageScore: number | null; passedPercent: number | null; overdue: number }
/** Per live launch: the people who would stop matching — `completed` ones keep it under Completed. */
export type AudienceLeavers = Array<{ launchId: string; courseId: string; title: string; people: Array<{ id: string; name: string; completed: boolean }> }>

export interface LearningItem {
  launchId: string; courseId: string; title: string; thumbnailUrl: string | null; description: string | null
  required: boolean; dueAt: string | null; receivedAt: string
  versions: Array<{ courseId: string; language: string; shareCode: string | null; title: string; slideCount: number }>
  progress: { status: string; language: string | null; versionCourseId: string | null; percent: number; currentSlide: number; furthestSlide?: number; startedAt?: string | null; score: number | null; passed: boolean | null; completedAt: string | null; lastActiveAt: string | null
    /** A completed course being taken again — that attempt's progress (12 Oct 2026). */
    retake?: { percent: number; currentSlide: number; furthestSlide?: number } | null } | null
  status: 'completed' | 'in_progress' | 'not_started' | 'overdue'
  quizNotPassed: boolean
  /** Set in the browser only (learnLocal): the learner has just left the course and the server has
   *  not caught up yet — the place is current, the percentage may not be. */
  progressPending?: boolean
}
/** Before this sitting (12 Oct 2026): each question's latest answer, each slide's saved state, and
 *  the course's outline for the learner's page. */
export interface LearnState {
  answers: Record<string, { correct: boolean; chosen: number | null }>
  saved?: Record<string, Record<string, unknown>>
  /** Slide by slide (by position) for this attempt, every sitting: seconds on it, narration parts heard. Viewed = present. */
  slides?: Record<number, { seconds: number; heard: number }>
  /** Slides (by position) finished in this attempt. */
  done?: number[]
  outline?: Array<{ title: string; slides: number; quiz: number }>
  passMark?: number
}
/** A notification as the server keeps it (org_notifications). */
export interface ServerNotification {
  id: string; kind: 'course_assigned' | 'due_reminder' | 'overdue' | 'org_invite' | 'translation_done' | 'info'
  orgId: string | null; title: string; body: string | null; link: string | null; createdAt: string; read: boolean
  /** An invitation's notification: the invitation it is about. */
  inviteId?: string
  /** An ownership transfer's notification: the transfer it is about (Accept / Decline in the bell). */
  transferId?: string
}
/** What a progress save answers — the finish screen reads it (12 Oct 2026). */
export interface ProgressResult {
  status?: string; percent?: number; score?: number | null; passed?: boolean | null; quizNotPassed?: boolean
  passMark?: number; attempts?: number; timeSpentS?: number; hasQuiz?: boolean; retake?: boolean
}
/** What the player reports (see launchModel LearningEvent). */
export type PlayerEvent =
  | { event: 'start'; at: string; language?: string; newAttempt?: boolean } | { event: 'slide'; at: string; slideIndex: number; seg?: number; slideId?: string; saved?: Record<string, unknown>; done?: boolean }
  | { event: 'quiz'; at: string; questionId: string; correct: boolean; chosen?: number } | { event: 'complete'; at: string }
  | { event: 'language'; at: string; language: string }
export interface ReportData {
  launches: Array<{ id: string; courseId: string; status: string; required: boolean; startAt: string }>
  targets: Array<{ id: string; launchId: string; kind: string; groupId: string | null; userId: string | null; rule: Condition[] | null }>
  rows: Array<ReportRow & {
    via: string | null
    /** The first attempt's quiz result — null until it was marked (Reports v2: the knowledge map).
     *  `answers`: question number (0-based, in course order) → right first time. */
    first: { score: number; passed: boolean; answers: Record<number, boolean> } | null
    /** Question number → how many times it was answered wrong, over every attempt. */
    missed: Record<number, number>
  }>
  courses: Array<{ id: string; title: string; thumbnailUrl: string | null; languages: string[]; slideCount: number; hasQuiz: boolean; passMark: number
    /** About how long it takes (from the narration), null when unknown. */
    minutes: number | null
    /** Each slide's name, in play order ('' when it has none). */
    slides: string[]
    /** Each quiz question's text, in course order. */
    questions: string[] }>
  people: Array<{ id: string; name: string; email: string; active: boolean }>
}

export interface AttrBundle {
  defs: AttrDef[]
  people: Person[]
  groups: Group[]
  eligible: Record<PersonList, string[]>
  csvColumns: string[] | null
}
export interface MyAttrs {
  defs: AttrDef[]
  values: Record<string, Value>
  /** Names of the people their Manager / Trainer / Coach point at. */
  names: Record<string, string>
  groups: Array<{ id: string; name: string }>
  roles: OrgRole[]
}
